import Button, { ActionButton } from '@bugbug/core/components/Button';
import { useTranslation } from 'react-i18next';

import useModal from '~/hooks/useModal';
import {
  selectSubscription,
  selectSubscriptionSchedule,
} from '~/modules/organization/organization.selectors';
import { useAppSelector } from '~/modules/store';
import { useCancelDowngradeMutation } from '~/modules/subscriptions/subscriptions.api';

import { getSubscriptionPlanLabel } from '../Subscription/Subscription.helpers';
import {
  SubscriptionModalContainer,
  SubscriptionModalContent,
  SubscriptionModalDetails,
  SubscriptionModalFooter,
  SubscriptionModalTitle,
} from '../Subscription/Subscription.styled';

import * as S from './CancelDowngradeModal.styled';

import CheckMarkIcon from '~/images/check-mark-icon.svg?react';
import QuestionMarkIcon from '~/images/question-mark-icon.svg?react';

export const CancelDowngradeModal = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'cancelDowngradeModal' });
  const modal = useModal();
  const activeSubscription = useAppSelector(selectSubscription);
  const subscriptionSchedule = useAppSelector(selectSubscriptionSchedule);
  const [cancelDowngrade, { isLoading, isSuccess }] = useCancelDowngradeMutation();

  const onSubmit = () => {
    cancelDowngrade();
  };

  if (!activeSubscription) return null;

  const activeParallelAddon = activeSubscription.addons.find(
    (addon) => addon.type === 'extra_parallel_runs_limit',
  );
  const scheduleParallelAddon = subscriptionSchedule?.addons.find(
    (addon) => addon.type === 'extra_parallel_runs_limit',
  );
  const includeBillingCycle =
    subscriptionSchedule?.billingCycle !== activeSubscription.billingCycle;

  if (isSuccess) {
    return (
      <SubscriptionModalContainer>
        <SubscriptionModalContent>
          <CheckMarkIcon width={48} height={48} />
          <SubscriptionModalDetails>
            <SubscriptionModalTitle>
              {t('success.title', 'Downgrade canceled')}
            </SubscriptionModalTitle>
            <S.Description>
              {t('success.description', "You're staying on your current plan.")}
            </S.Description>
          </SubscriptionModalDetails>
        </SubscriptionModalContent>
        <SubscriptionModalFooter>
          <Button variant="primary" onClick={modal.hide}>
            {t('done', 'Done')}
          </Button>
        </SubscriptionModalFooter>
      </SubscriptionModalContainer>
    );
  }

  if (!subscriptionSchedule) return null;

  return (
    <SubscriptionModalContainer>
      <SubscriptionModalContent>
        <QuestionMarkIcon width={48} height={48} />
        <SubscriptionModalDetails>
          <SubscriptionModalTitle>
            {t('confirm.title', 'Cancel your upcoming downgrade?')}
          </SubscriptionModalTitle>
          <S.Description>
            {t(
              'confirm.description',
              'Your upcoming downgrade from {{currentPlan}} to {{incomingPlan}} will be canceled. Are you sure?',
              {
                currentPlan: getSubscriptionPlanLabel({
                  plan: activeSubscription,
                  parallels: activeParallelAddon?.value ?? 0,
                  includeBillingCycle,
                }),
                incomingPlan: getSubscriptionPlanLabel({
                  plan: subscriptionSchedule,
                  parallels: scheduleParallelAddon?.value ?? 0,
                  includeBillingCycle,
                }),
              },
            )}
          </S.Description>
        </SubscriptionModalDetails>
      </SubscriptionModalContent>
      <SubscriptionModalFooter>
        <Button variant="primary" bordered onClick={modal.hide} disabled={isLoading}>
          {t('cancel', 'No')}
        </Button>
        <ActionButton variant="primary" onClick={onSubmit} pending={isLoading}>
          {t('button.confirm', 'Yes, cancel downgrade')}
        </ActionButton>
      </SubscriptionModalFooter>
    </SubscriptionModalContainer>
  );
};
